<template>
  <div class="base-input-wrapper" :class="{ 'has-error': error, 'has-value': hasValue, 'is-focused': isFocused }">
    <label v-if="label" :for="inputId" class="base-input-label">
      {{ label }}
      <span v-if="required" class="text-danger dark:text-danger-dark ml-0.5">*</span>
    </label>

    <div class="base-input-container" :class="containerClass">
      <!-- Left Icon -->
      <span v-if="leftIcon" class="base-input-icon left">
        <component :is="leftIcon" class="h-5 w-5" />
      </span>

      <input
        :id="inputId"
        :type="computedType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :name="name"
        class="base-input-field"
        :class="[inputClass, { 'has-left-icon': leftIcon, 'has-right-icon': rightIcon || type === 'password' }]"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />

      <!-- Right Icon / Password Toggle -->
      <button
        v-if="type === 'password'"
        type="button"
        class="base-input-icon right base-input-toggle"
        @click="showPassword = !showPassword"
        tabindex="-1"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
      >
        <Eye v-if="!showPassword" class="h-5 w-5" />
        <EyeOff v-else class="h-5 w-5" />
      </button>

      <span v-else-if="rightIcon" class="base-input-icon right">
        <component :is="rightIcon" class="h-5 w-5" />
      </span>
    </div>

    <!-- Hint text -->
    <p v-if="hint && !error" class="base-input-hint">{{ hint }}</p>

    <!-- Error message -->
    <p v-if="error" class="base-input-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Component } from 'vue'
import { Eye, EyeOff } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  modelValue: string
  label?: string
  type?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: string
  hint?: string
  name?: string
  autocomplete?: string
  leftIcon?: Component
  rightIcon?: Component
  inputClass?: string
  containerClass?: string
}>(), {
  type: 'text',
  disabled: false,
  required: false,
  autocomplete: 'off'
})

defineEmits<{
  'update:modelValue': [value: string]
}>()

const inputId = `input-${Math.random().toString(36).slice(2, 9)}`
const showPassword = ref(false)
const isFocused = ref(false)

const computedType = computed(() => {
  if (props.type === 'password') {
    return showPassword.value ? 'text' : 'password'
  }
  return props.type
})

const hasValue = computed(() => props.modelValue && props.modelValue.length > 0)
</script>

<style scoped>
.base-input-wrapper {
  @apply w-full;
}

.base-input-label {
  @apply block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5;
  @apply transition-colors duration-200;
}

.base-input-container {
  @apply relative flex items-center;
}

.base-input-field {
  @apply w-full px-4 py-2.5 rounded-xl
         border border-gray-300 dark:border-gray-600
         bg-white dark:bg-gray-800
         text-gray-900 dark:text-gray-100
         placeholder-gray-400 dark:placeholder-gray-500
         text-sm
         transition-all duration-200 ease-in-out
         focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary
         dark:focus:ring-primary-dark/40 dark:focus:border-primary-dark
         disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900;
}

.base-input-field.has-left-icon {
  @apply pl-11;
}

.base-input-field.has-right-icon {
  @apply pr-11;
}

/* States */
.has-error .base-input-field {
  @apply border-danger dark:border-danger-dark
         focus:ring-danger/40 dark:focus:ring-danger-dark/40
         focus:border-danger dark:focus:border-danger-dark;
}

.has-error .base-input-label {
  @apply text-danger dark:text-danger-dark;
}

.is-focused .base-input-field {
  @apply ring-2 ring-primary/40 border-primary
         dark:ring-primary-dark/40 dark:border-primary-dark;
}

.has-error.is-focused .base-input-field {
  @apply ring-danger/40 border-danger
         dark:ring-danger-dark/40 dark:border-danger-dark;
}

/* Icons */
.base-input-icon {
  @apply absolute inset-y-0 flex items-center pointer-events-none;
}

.base-input-icon.left {
  @apply left-0 pl-3.5;
}

.base-input-icon.right {
  @apply right-0 pr-3.5;
}

.base-input-icon :deep(svg) {
  @apply text-gray-400 dark:text-gray-500;
  @apply transition-colors duration-200;
}

.has-error .base-input-icon :deep(svg) {
  @apply text-danger/60 dark:text-danger-dark/60;
}

/* Password toggle */
.base-input-toggle {
  @apply pointer-events-auto cursor-pointer;
  @apply hover:text-gray-600 dark:hover:text-gray-300;
}

/* Hint & Error text */
.base-input-hint {
  @apply mt-1.5 text-xs text-gray-500 dark:text-gray-400;
}

.base-input-error {
  @apply mt-1.5 text-xs text-danger dark:text-danger-dark font-medium;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
